import * as React from "react";

import { Input, Radio, Form, Select } from "antd";
import { Page, PageHero, Spinner } from "src/components/common";
import DropdownInputAnt from "src/components/forms/DropdownInputAnt";
import CustomFieldOptions from "src/components/forms/CustomFieldOptions";

import CustomFieldAPI from "./CustomFieldAPI";

import { Validation } from "src/common/utils";
import DatasetAPI from "../Datasets/DatasetAPI";

import PickListContext from "src/context/pickListContext";
import AuthorizationWrapper from "src/common/AuthorizationWrapper";
import { Button } from "src/components/buttons";
import { AdminPermissions } from "src/common/Permissions";
import { toast } from "react-toastify";

interface IOption {
	key: string;
	label: string;
}

interface IProps {
	datasets: DatasetDTO[];
	history?: any;
	match?: any;
}

interface IState {
	customField: CustomFieldDTO;
	datasets: DatasetDTO[];
	iconLoading: boolean;
	loading: boolean;
	showOptions: boolean;
	disabled: boolean;
	isLoading: boolean;
}

class OrganizationCustomFieldsEdit extends React.Component<IProps, IState> {
	public static contextType = PickListContext;
	public defaultCustomField = {
		id: undefined,
		name: "",
		placeholderText: undefined,
		controlTypeId: undefined,
		datasetIds: [],
		showInListView: undefined,
		customFieldType: undefined,
		reportPositionId: undefined,
		includeInExport: undefined,
		required: undefined,
		phi: undefined,
		includeInBackendReport: undefined,
		isActive: true,
		clientId: undefined,
		options: [],
	} as CustomFieldDTO;

	public state: IState = {
		customField: this.defaultCustomField,
		datasets: [],
		iconLoading: false,
		loading: false,
		showOptions: false,
		disabled: false,
		isLoading: true,
	};

	public componentDidMount() {
		const orgId = this.props.match.params.organizationId;
		const customFieldId = Number(this.props.match.params.customFieldId);
		if (customFieldId !== 0) {
			CustomFieldAPI.getCustomField(orgId, customFieldId).then(
				(res: CustomFieldDTO) => {
					if (res.options !== undefined && res.options.length > 0) {
						this.setState({ showOptions: true });
					}
					if (
						res.controlTypeId !== undefined &&
						res.customFieldType !== undefined
					) {
						this.setState({ disabled: true });
					}
					this.setState({ customField: res, isLoading: false });
				}
			);
		}

		DatasetAPI.getOrganizationDatasets(orgId).then((res) =>
			this.setState({ datasets: res, isLoading: false })
		);
	}

	public render() {
		const { customField, datasets, loading } = this.state;
		const validCustomField = {
			customFieldName: Validation.validateStringIsNotEmpty(
				customField.name!,
				"Please enter a name"
			),
			kind: Validation.validateIfANumberOrNumbers(customField.controlTypeId!),
			customFieldType: Validation.validateIfANumberOrNumbers(
				customField.customFieldType!
			),
			showInReport: Validation.validateIsActive(customField.showInReport!),
			includeInExport: Validation.validateIsActive(
				customField.includeInExport!
			),
			required: Validation.validateIsActive(customField.required!),
			phi: Validation.validateIsActive(customField.phi!),
			isActive: Validation.validateIsActive(customField.isActive!),
			showInListView: Validation.validateIsActive(customField.showInListView!),
			options: Validation.validateCustomFieldHasOneOption(
				customField.options!,
				customField.controlTypeId!
			),
		};

		const newDatasetValues = this.convertDatasetValue(datasets);
		const customFieldDatasetValue = this.setDatasetValues(
			customField.datasetIds!,
			datasets
		);
		return this.state.isLoading !== false ? (
			<Spinner size="full-page" />
		) : (
			<Page title="Custom fields">
				<PageHero
					title={`${this.state.customField.name || "New"} custom field`}
					parent="Custom fields"
					parentRoute={`/admin/organizations/${this.props.match.params.organizationId}/custom-fields`}
				/>
				<div className="container-fluid">
					<div className="row">
						<div className="col-3 form-group">
							<label htmlFor="name">Name *</label>
							<Form.Item
								hasFeedback={true}
								validateStatus={
									validCustomField.customFieldName.validationStatus
								}
								help={validCustomField.customFieldName.message}
								required={true}
							>
								<AuthorizationWrapper
									type="disable"
									permissions={[AdminPermissions.EditOrganizationCustomField]}
								>
									<Input
										placeholder="Enter a custom field name"
										id="name"
										name="name"
										value={customField.name!}
										onChange={this.onCustomFieldChange}
									/>
								</AuthorizationWrapper>
							</Form.Item>
						</div>
						<div className="col-3 form-group">
							<label>Control type *</label>
							<Form.Item
								hasFeedback={true}
								validateStatus={validCustomField.kind.validationStatus}
								help={validCustomField.kind.message}
								required={true}
							>
								<AuthorizationWrapper
									type="disable"
									permissions={[AdminPermissions.EditOrganizationCustomField]}
								>
									<DropdownInputAnt
										name="controlTypeId"
										required={true}
										value={customField.controlTypeId!}
										disabled={this.state.disabled}
										placeholder="Select what control type the custom field will be"
										pickListCode="ControlType"
										onChange={this.onControlTypeChange}
									/>
								</AuthorizationWrapper>
							</Form.Item>
						</div>
						<div className="col-3 form-group">
							<label>Custom field type *</label>
							<Form.Item
								hasFeedback={true}
								validateStatus={
									validCustomField.customFieldType.validationStatus
								}
								help={validCustomField.customFieldType.message}
								required={true}
							>
								<AuthorizationWrapper
									type="disable"
									permissions={[AdminPermissions.EditOrganizationCustomField]}
								>
									<DropdownInputAnt
										name="inputPosition"
										required={true}
										value={customField.customFieldType}
										disabled={this.state.disabled}
										placeholder="Select what this custom field will be attached to"
										pickListCode="CustomFieldType"
										onChange={this.onCustomFieldTypeChange} // create new onchange handler for this dropdown
									/>
								</AuthorizationWrapper>
							</Form.Item>
						</div>
					</div>
					<div className="form-group">
						{this.state.showOptions === true ? (
							<CustomFieldOptions
								value={this.state.customField.options!}
								onAdd={this.onAdd}
								onChange={this.onOptionsChange}
								onStatusChange={this.onOptionsStatusChange}
								onRemove={this.onRemove}
							/>
						) : null}
					</div>
					<div className="row">
						<div className="col-3 form-group">
							<label>Dataset</label>
							<Form.Item>
								<AuthorizationWrapper
									type="disable"
									permissions={[AdminPermissions.EditOrganizationCustomField]}
								>
									<Select
										labelInValue={true}
										mode="multiple"
										showArrow={true}
										value={customFieldDatasetValue!}
										onChange={(value: any) =>
											this.customFieldDatasetChange(value)
										}
									>
										{this.state.datasets.map((d) => (
											<Select.Option key={d.id!.toString()}>
												{d.name}
											</Select.Option>
										))}
									</Select>
								</AuthorizationWrapper>
							</Form.Item>
						</div>
						{!this.state.showOptions ? (
							<div className="col-3 form-group">
								<label htmlFor="name">Placeholder text</label>
								<Form.Item>
									<AuthorizationWrapper
										type="disable"
										permissions={[AdminPermissions.EditOrganizationCustomField]}
									>
										<Input
											placeholder="Enter some placeholder text here"
											id="placeholderText"
											name="placeholderText"
											onChange={this.onCustomFieldChange}
											value={customField.placeholderText!}
										/>
									</AuthorizationWrapper>
								</Form.Item>

								<p className="text-muted">Shown when the field is left blank</p>
							</div>
						) : null}
					</div>
					<div className="row">
						<div className="col-3 form-group">
							<label>Export</label>
							<Form.Item
								hasFeedback={true}
								required={true}
								validateStatus={
									validCustomField.includeInExport.validationStatus
								}
								help={validCustomField.includeInExport.message}
							>
								<AuthorizationWrapper
									type="disable"
									permissions={[AdminPermissions.EditOrganizationCustomField]}
								>
									<Radio.Group
										id="export"
										value={customField.includeInExport}
										name="includeInExport"
										onChange={this.onCustomFieldChange}
									>
										{[
											{ label: "Yes", value: true },
											{ label: "No", value: false },
										].map((item, index) => {
											return (
												<Radio key={index} value={item.value}>
													{item.label}
												</Radio>
											);
										})}
									</Radio.Group>
								</AuthorizationWrapper>
							</Form.Item>
						</div>
						<div className="col-3 form-group">
							<label>Required</label>
							<div>
								<Form.Item
									hasFeedback={true}
									required={true}
									validateStatus={validCustomField.required.validationStatus}
									help={validCustomField.required.message}
								>
									<AuthorizationWrapper
										type="disable"
										permissions={[AdminPermissions.EditOrganizationCustomField]}
									>
										<Radio.Group
											id="required"
											name="required"
											value={customField.required}
											onChange={this.onCustomFieldChange}
										>
											{[
												{ label: "Yes", value: true },
												{ label: "No", value: false },
											].map((item, index) => {
												return (
													<Radio key={index} value={item.value}>
														{item.label}
													</Radio>
												);
											})}
										</Radio.Group>
									</AuthorizationWrapper>
								</Form.Item>
							</div>
						</div>
						<div className="col-3 form-group">
							<label>PII/PHI</label>
							<Form.Item
								hasFeedback={true}
								required={true}
								validateStatus={validCustomField.phi.validationStatus}
								help={validCustomField.phi.message}
							>
								<AuthorizationWrapper
									type="disable"
									permissions={[AdminPermissions.EditOrganizationCustomField]}
								>
									<Radio.Group
										id="PiiPhi"
										name="phi"
										value={customField.phi}
										onChange={this.onCustomFieldChange}
									>
										{[
											{ label: "Yes", value: true },
											{ label: "No", value: false },
										].map((item, index) => {
											return (
												<Radio key={index} value={item.value}>
													{item.label}
												</Radio>
											);
										})}
									</Radio.Group>
								</AuthorizationWrapper>
							</Form.Item>
						</div>
					</div>
					<div className="row">
						<div className="col-3 form-group">
							<label>Status</label>
							<Form.Item
								hasFeedback={true}
								required={true}
								validateStatus={validCustomField.isActive.validationStatus}
								help={validCustomField.isActive.message}
							>
								<AuthorizationWrapper
									type="disable"
									permissions={[AdminPermissions.EditOrganizationCustomField]}
								>
									<Radio.Group
										id="isActive"
										name="isActive"
										value={customField.isActive!}
										onChange={this.onCustomFieldChange}
									>
										{[
											{ label: "Active", value: true },
											{ label: "InActive", value: false },
										].map((item, index) => {
											return (
												<Radio key={index} value={item.value}>
													{item.label}
												</Radio>
											);
										})}
									</Radio.Group>
								</AuthorizationWrapper>
							</Form.Item>
						</div>
						<div className="col-3 form-group">
							<label>Show in list view</label>
							<Form.Item
								hasFeedback={true}
								help={validCustomField.showInListView.message}
								validateStatus={
									validCustomField.showInListView.validationStatus
								}
								required={true}
							>
								<AuthorizationWrapper
									type="disable"
									permissions={[AdminPermissions.EditOrganizationCustomField]}
								>
									<Radio.Group
										id="showInListView"
										name="showInListView"
										value={customField.showInListView}
										onChange={this.onCustomFieldChange}
									>
										{[
											{ label: "Yes", value: true },
											{ label: "No", value: false },
										].map((item) => (
											<Radio key={item.label} value={item.value}>
												{item.label}
											</Radio>
										))}
									</Radio.Group>
								</AuthorizationWrapper>
							</Form.Item>
						</div>
						<div className="col-3 form-group">
							<label>Show in report</label>
							<Form.Item
								hasFeedback={true}
								help={validCustomField.showInReport.message}
								validateStatus={validCustomField.showInReport.validationStatus}
								required={true}
							>
								<AuthorizationWrapper
									type="disable"
									permissions={[AdminPermissions.EditOrganizationCustomField]}
								>
									<Radio.Group
										id="showInReport"
										name="showInReport"
										value={customField.showInReport!}
										onChange={this.onCustomFieldChange}
									>
										{[
											{ label: "Yes", value: true },
											{ label: "No", value: false },
										].map((item) => (
											<Radio key={item.label} value={item.value}>
												{item.label}
											</Radio>
										))}
									</Radio.Group>
								</AuthorizationWrapper>
							</Form.Item>
						</div>
					</div>
					<div className="row">
						<div className="col">
							<AuthorizationWrapper
								type="hide"
								permissions={[AdminPermissions.EditOrganizationCustomField]}
							>
								<Form.Item
									hasFeedback={true}
									help={validCustomField.options.message}
									validateStatus={validCustomField.options.validationStatus}
									required={true}
								>
									<Button
										disabled={!this.isValid(validCustomField) || loading}
										className="btn-primary"
										loading={this.state.loading}
										onClick={this.submitCustomField}
									>
										Submit
									</Button>
								</Form.Item>
							</AuthorizationWrapper>
						</div>
					</div>
				</div>
			</Page>
		);
	}

	private setDatasetValues = (datasetIds: number[], dataSets: DatasetDTO[]) => {
		const newDatasetValues: any[] = [];

		if (datasetIds.length > 0) {
			dataSets.filter((dSet) => {
				if (datasetIds.indexOf(dSet.id!) !== -1) {
					newDatasetValues.push({
						key: dSet.id!.toString(),
						label: dSet.name!,
					});
				}
			});
			return newDatasetValues;
		} else {
			return [];
		}
	};

	private onCustomFieldChange = (e: any) => {
		let value = e.target.value;
		if (value === "true") {
			value = true;
		}
		if (value === "false") {
			value = false;
		}
		let name = e.target.name;
		if (e.target.type === "radio") {
			value = value;
		} else if (e.target.type === "checkbox") {
			if (e.target.data !== undefined) {
				// If there's data then it's a checkbox group
				if (e.target.data.singleSelect) {
					value = value;
				} else {
					// Don't split if single value
					if (value === "") {
						value = [];
					} else {
						value = value.split(",");
					}
				}
				name = e.target.data.name;
			} else {
				value = e.target.checked;
			}
		}
		const customField = {
			...this.state.customField,
			[name]: value,
		};
		this.setState({ customField });
	};

	private convertDatasetValue = (datasets: DatasetDTO[]) => {
		const newDatasetValues = datasets.map((d) => ({
			key: d.id!.toString(),
			label: d.name,
		}));
		return newDatasetValues;
	};

	private onRemove = (id: number) => {
		const { customField } = this.state;
		const filteredOptions = customField.options!.filter(
			(opt, index) => index !== id
		);
		customField.options = filteredOptions;
		this.setState({ customField });
	};

	private onAdd = () => {
		const { customField } = this.state;
		const newOption = { options: undefined, active: true };
		customField.options!.push(newOption);
		this.setState({ customField });
	};

	private onOptionsChange = (event: any) => {
		const { customField } = this.state;
		const indexValue = Number(event.target.id);
		const inputFieldOption = { options: event.target.value };

		const foundOption = customField.options!.find(
			(opt, index) => index === indexValue
		);
		if (foundOption) {
			foundOption.options = inputFieldOption.options;
		}
		this.setState({ customField });
	};

	private onOptionsStatusChange = (event: any) => {
		const { customField } = this.state;
		const indexValue = Number(event.target.id);
		const inputFieldOption = { active: event.target.value };

		const foundOption = customField.options!.find(
			(opt, index) => index === indexValue
		);
		if (foundOption) {
			foundOption.active = inputFieldOption.active;
		}
		this.setState({ customField });
	};

	private onControlTypeChange = (value: any) => {
		const controlTypeValue = value.key;
		const picklistItemType: PickListItemResponseDTO =
			this.context.pickListItems.find(
				(item: PickListItemResponseDTO) => item.id! === value.key
			);

		if (picklistItemType.value1 !== undefined) {
			this.setState({ showOptions: true });
		} else if (picklistItemType.value1 === undefined) {
			// tslint:disable-next-line:no-shadowed-variable
			const { customField } = this.state;
			customField.options = [];
			this.setState({ showOptions: false, customField });
		}

		const customField = {
			...this.state.customField,
			controlTypeId: controlTypeValue,
		};

		this.setState({ customField });
	};

	private customFieldDatasetChange = (values: any[]) => {
		const datasetIds = values.map((val) => Number(val.key));
		const customField = {
			...this.state.customField,
			datasetIds,
		};
		this.setState({ customField });
	};

	private onCustomFieldTypeChange = (value: any) => {
		const customFieldType = +value.key;
		const customField = { ...this.state.customField, customFieldType };
		this.setState({ customField });
	};

	private isValid = (validCustomField: any) => {
		const validateValues: string[] = Object.keys(validCustomField).map(
			(key) => validCustomField[key].validationStatus
		);
		return Validation.isValid(validateValues);
	};

	private submitCustomField = () => {
		this.setState({ loading: true });
		const customField = {
			...this.state.customField,
			id:
				this.state.customField.id !== undefined
					? this.state.customField.id
					: undefined,
			organizationId: this.props.match.params.organizationId,
		};
		const organizationId = Number(this.props.match.params.organizationId);
		CustomFieldAPI.updateCustomField(organizationId, customField)
			.then(() => {
				this.props.history.push(
					`/admin/organizations/${organizationId}/custom-fields`
				);
				this.setState({ loading: false });
			})
			.catch((error) => {
				toast.error(error.response.data.responseStatus.message);
				this.setState({ loading: false });
			});
	};
}

export default OrganizationCustomFieldsEdit;
